import axios from 'axios'
import { GetBaseUrl } from '@/utils/ConfigHelper'
import Response from '../../../Common/Response'
import { PUBLIC_ERROR_CODE } from '../../../../Error/Constants'
import { GetPublicErrorCode } from '../../../../Error/Handler'
import Validator from '@/utils/Validator'

/**
 * 전체 주문 내역 조회 API
 * @param {String} adminToken 
 * @param {String} startDate 
 * @param {String} endDate 
 * @param {Number | null} offset 
 * @param {Number | null} limit 
 * @returns
 */
const GetOrderList = async (
    adminToken,
    startDate,
    endDate,
    offset = null,
    limit = null
) => {
    try {
        const url = `${GetBaseUrl()}/apis/admin/video09/orders`
        const params = { startDate }
        if (offset) params.offset = offset
        if (limit) params.limit = limit

        const value = new Date(endDate)
        value.setDate(value.getDate() + 1)
        value.setMilliseconds(value.getMilliseconds() - 1)
        params.endDate = value.toISOString()

        const config = {
            params,
            headers: { Authorization: `Bearer ${adminToken}` }
        }
        const response = await axios.get(url, config)

        const result = (response.status === 200)
        const data = response.data.data
        return new Response(result, data)
    } catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
            case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
            case PUBLIC_ERROR_CODE.ERR_NOT_FOUND:
                return new Response(false, publicErrorCode)
            case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
            default:
                throw e
        }
    }
}

/**
 * 특정 주문건 1건을 조회하는 API
 * @param {String} adminToken 
 * @param {String} orderId 
 * @returns 
 */
const GetOrderItem = async (
    adminToken,
    orderId
) => {
    try {
        const url = `${GetBaseUrl()}/apis/admin/video09/orders/${orderId}`
        const config = {
            headers: { Authorization: `Bearer ${adminToken}` }
        }
        const response = await axios.get(url, config)

        const result = (response.status === 200)
        const data = response.data.data
        return new Response(result, data)
    } catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
            case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
            case PUBLIC_ERROR_CODE.ERR_NOT_FOUND:
                return new Response(false, publicErrorCode)
            case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
            default:
                throw e
        }
    }
}

/**
 * 카페 24 회원이 결제한 내역의 xlsx 데이터를 blobUrl 형태로 가져오는 API
 * @param {String} adminToken required
 * @param {{
 *  startDate: Date | null
 *  endDate: Date | null
 *  dateType: Number | null
 * }} data 
 */
const GetCafe24Xlsx = async (adminToken, data = {}) => {
    try {
        const { startDate, endDate, dateType } = data

        const url = `${GetBaseUrl()}/apis/admin/video09/orders/xlsx/cafe24`
        const params = { date_type: dateType }

        params.start_time = new Date(startDate).toISOString()
        const value = new Date(endDate)
        value.setDate(value.getDate() + 1)
        value.setMilliseconds(value.getMilliseconds() - 1)
        params.end_time = value.toISOString()

        const config = {
            params,
            headers: { Authorization: `Bearer ${adminToken}` },
            responseType: 'blob'
        }
        const response = await axios.get(url, config)
        const result = (response.status === 200)
        const resultData = response.data
        return new Response(result, resultData)
    }
    catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
            case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
            default:
                throw e
        }
    }
}

/**
 * 
 * @param {String} adminToken 
 * @param {Boolean | null} orderCheck 
 * @param {Boolean | null} sendCheck 
 * @returns 
 */
const UpdateOrderData = async (
    adminToken,
    orderId,
    orderCheck = null, 
    sendCheck = null
) => {
    try {
        const url = `${GetBaseUrl()}/apis/admin/video09/orders/${orderId}`
        const body = {
            orderCheck,
            sendCheck
        }
        const config = {
            headers: { Authorization: `Bearer ${adminToken}` }
        }
        const response = await axios.patch(url, body, config)

        const result = (response.status === 200)
        const data = response.data.data
        return new Response(result, data)
    } catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
            case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
            case PUBLIC_ERROR_CODE.ERR_NOT_FOUND:
                return new Response(false, publicErrorCode)
            case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
            default:
                throw e
        }
    }
}

/**
 * 
 * @param {String} adminToken 
 * @param {String} orderNo 
 * @param {String} buyerEmail 
 * @param {String} amount 
 * @returns 
 */
const UpdatePaymentData = async (
    adminToken,
    orderNo,
    buyerEmail,
    amount
) => {
    try {
        const url = `${GetBaseUrl()}/apis/admin/video09/orders/${orderNo}/payment-data`
        const body = { buyerEmail, amount }
        const config = { headers: { Authorization: `Bearer ${adminToken}` }}
        const response = await axios.patch(url, body, config)
        const result = (response.status === 200)
        const data = response.data.data
        return new Response(result, data)
    } catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
            case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
            case PUBLIC_ERROR_CODE.ERR_NOT_FOUND:
                return new Response(false, publicErrorCode)
            case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
            default:
                throw e
        }
    }
}

/**
 * @param {String} adminToken
 * @param {{
 *  orderId: String
 *  comments: Array<String>
 * }} data 
 * @throws {Error}
 * @returns {Promise<Response>}
 */
const UpdateCommentData = async ( adminToken, data = {}) => {
    try {
        const url = `${GetBaseUrl()}/apis/admin/video09/orders/${data.orderId}/comments`
        const body = { comments: data.comments }
        const config = { headers: { Authorization: `Bearer ${adminToken}` }}
        const response = await axios.patch(url, body, config)

        const result = (response.status === 200)
        const resultData = response.data.data;
        return new Response(result, resultData)
    } catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
            case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
            case PUBLIC_ERROR_CODE.ERR_NOT_FOUND:
                return new Response(false, publicErrorCode)
            case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
            default:
                throw e
        }
    }
}

export default {
    GetOrderList,
    GetOrderItem,
    GetCafe24Xlsx,
    UpdateOrderData,
    UpdatePaymentData,
    UpdateCommentData
}